/* One dashboard widget's box: header (icon, title, badge, meta, link), the body,
   and while customising the edit bar (grip, title, size, settings, remove), the
   corner resize handle and the "Min w × h" pill. A list widget that passes `fit`
   shrinks to its rows, so the grid packs the next widget into the space below;
   its set height stays the most it grows to. */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { C } from '../components/ChatAssistant/compact/tokens';
import { font as f } from '../components/ChatAssistant/compact/style';
import { Icon } from '../components/ChatAssistant/compact/kit';
import { GAP, HEADER_H, ROW, TYPES, type Widget } from './model';

/** What the page hands every widget: its box and the editing hooks. */
export interface FrameProps {
  widget: Widget;
  /** Title shown (the person's own, or the type's). */
  title: string;
  width: number;
  height: number;
  /** Columns spanned in the CSS grid (1 on phones). */
  span: number;
  editing: boolean;
  selected: boolean;
  dragging: boolean;
  atMin: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onResizeStart: (e: React.PointerEvent) => void;
  onSettings: () => void;
  onRemove: () => void;
}

interface Props extends FrameProps {
  meta?: ReactNode;
  badge?: number;
  link?: { to: string; label: string; minWidth?: number };
  /** Extra header controls (search, ⋯), hidden while customising. */
  tools?: ReactNode;
  /** The body's natural height in pixels, for a widget whose content can be shorter than its box. */
  fit?: number;
  children: ReactNode;
}

/** Grid rows to span: the set height, or fewer when `fit` content needs less (never while customising). */
function spanRows(p: Props) {
  const h = p.widget.h;
  if (p.fit == null || p.editing) return h;
  const px = p.fit + 2 + (p.widget.cfg.hdr ? HEADER_H : 0);
  const need = Math.ceil((px + GAP) / (ROW + GAP));
  return Math.min(h, Math.max(TYPES[p.widget.type].min[1], need));
}

export function WidgetFrame(p: Props) {
  const { t } = useTranslation('dashboardWidgets');
  const spec = TYPES[p.widget.type];
  const showLink = p.link && !p.editing && p.width >= (p.link.minWidth ?? 300);
  return (
    <div
      className="dbw-frame"
      data-widget={p.widget.type}
      onDragOver={p.onDragOver}
      style={{
        position: 'relative', gridColumn: `span ${p.span}`, gridRow: `span ${spanRows(p)}`, minWidth: 0, minHeight: 0, boxSizing: 'border-box',
        background: C.card, border: p.editing ? `1px dashed ${C.tintLine}` : `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden',
        display: 'flex', flexDirection: 'column', opacity: p.dragging ? 0.35 : 1, boxShadow: p.selected ? `0 0 0 2px ${C.purple}` : 'none',
      }}
    >
      {p.widget.cfg.hdr && (
        <div style={{ height: HEADER_H, flex: 'none', padding: '0 14px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${C.border}`, boxSizing: 'border-box', minWidth: 0 }}>
          <Icon name={spec.icon} size={14} color={C.purpleLt} />
          <span style={{ flex: '0 1 auto', maxWidth: '65%', font: f(600, 13.5), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</span>
          {!!p.badge && (
            <span style={{ height: 18, minWidth: 18, padding: '0 6px', boxSizing: 'border-box', borderRadius: 99, background: C.purple, color: '#fff', font: f(700, 10.5, 18 / 10.5), textAlign: 'center', flex: 'none' }}>{p.badge}</span>
          )}
          <span style={{ flex: '1 1 0', minWidth: 0, font: f(500, 12), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.meta}</span>
          {!p.editing && p.tools}
          {showLink && p.link && (
            <Link to={p.link.to} className="dbw-link" style={{ font: f(500, 12), textDecoration: 'none', whiteSpace: 'nowrap', color: C.purpleLt }}>{p.link.label}</Link>
          )}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', pointerEvents: p.editing ? 'none' : 'auto' }}>
        {p.children}
      </div>

      {p.editing && (
        <>
          <div
            draggable
            onDragStart={p.onDragStart}
            onDragEnd={p.onDragEnd}
            title={t('edit.drag')}
            style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 34, padding: '0 6px 0 10px', display: 'flex', alignItems: 'center', gap: 8, background: 'color-mix(in srgb, var(--om-surface) 96%, transparent)', borderBottom: `1px solid ${C.borderHi}`, cursor: 'grab', boxSizing: 'border-box' }}
          >
            <Icon name="more-vertical" size={14} color={C.muted} />
            <Icon name={spec.icon} size={13} color={C.purpleLt} />
            <span style={{ flex: 1, minWidth: 0, font: f(600, 12.5), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</span>
            <span style={{ font: f(600, 11), color: C.sub, whiteSpace: 'nowrap' }}>{p.widget.w} × {p.widget.h}</span>
            <button type="button" className="dbw-icon-btn" onClick={p.onSettings} title={t('edit.settings')} aria-label={t('edit.settings')} style={{ background: p.selected ? C.tint20 : 'transparent', color: C.purplePale }}>
              <Icon name="settings" size={14} />
            </button>
            <button type="button" className="dbw-icon-btn" onClick={p.onRemove} title={t('edit.remove')} aria-label={t('edit.remove')} style={{ color: C.sub }}>
              <Icon name="x" size={14} />
            </button>
          </div>
          <div
            onPointerDown={p.onResizeStart}
            title={t('edit.resize')}
            className="dbw-resize"
            style={{ position: 'absolute', right: 0, bottom: 0, width: 22, height: 22, cursor: 'nwse-resize', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: 4, boxSizing: 'border-box', touchAction: 'none' }}
          >
            <span style={{ width: 10, height: 10, borderRight: `2px solid ${C.purpleLt}`, borderBottom: `2px solid ${C.purpleLt}`, borderRadius: '0 0 3px 0' }} />
          </div>
          {p.atMin && (
            <span style={{ position: 'absolute', left: 10, bottom: 8, height: 20, padding: '0 8px', display: 'flex', alignItems: 'center', borderRadius: 99, background: C.tint20, color: C.purplePale, font: f(600, 10.5), whiteSpace: 'nowrap' }}>
              {t('edit.min', { size: `${spec.min[0]} × ${spec.min[1]}` })}
            </span>
          )}
        </>
      )}
    </div>
  );
}

/** Centered message inside a widget body: loading, a failure with Retry, no access, empty. */
export function BodyNote({ icon, children, action }: { icon?: string; children: ReactNode; action?: { label: string; onClick: () => void } }) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 12, textAlign: 'center' }}>
      {icon && <Icon name={icon} size={18} color={C.purpleLt} />}
      <span style={{ font: f(500, 12.5), color: C.sub }}>{children}</span>
      {action && (
        <button type="button" className="dbw-ghost-btn" onClick={action.onClick} style={{ height: 26, padding: '0 10px', border: `1px solid ${C.borderHi}`, borderRadius: 6, background: 'transparent', color: C.purpleLt, font: f(500, 12), cursor: 'pointer' }}>
          {action.label}
        </button>
      )}
    </div>
  );
}
